import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { Mail, Phone, Building2, Users, Search, Filter, MapPin } from 'lucide-react';

const staff = [
  { name: 'Prof. Jean-Pierre Nkoulou', title: 'Director General', department: 'Administration', email: 'director@estad.edu', phone: '+237 233 123 400', campus: 'Yaoundé Central' },
  { name: 'Dr. Marie-Claire Ngassa', title: 'Dean of Academics', department: 'Academic Affairs', email: 'dean@estad.edu', phone: '+237 233 123 401', campus: 'Yaoundé Central' },
  { name: 'Prof. Alain Mballa', title: 'Head of Research', department: 'Research & Innovation', email: 'research@estad.edu', phone: '+237 233 123 402', campus: 'Yaoundé Central' },
  { name: 'Dr. Isabelle Fouda', title: 'Head of Telecommunications', department: 'Engineering', email: 'telecom@estad.edu', phone: '+237 233 123 403', campus: 'Yaoundé Central' },
  { name: 'Prof. Samuel Tchinda', title: 'Head of IT Department', department: 'Information Technology', email: 'it@estad.edu', phone: '+237 233 123 404', campus: 'Douala Tech' },
  { name: 'Dr. Françoise Essomba', title: 'Student Affairs Director', department: 'Student Services', email: 'student.affairs@estad.edu', phone: '+237 233 123 405', campus: 'Yaoundé Central' }
];

export default function StaffDirectory() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('sidebar_resources_label', 'Staff Directory')}
        description={t('directory_hero_desc', 'Find contact information for faculty and staff across all ESTAD campuses.')}
        breadcrumbs={[{ label: t('sidebar_resources_label', 'Directory'), href: '/staff-directory' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-20 px-6 lg:px-12 flex flex-col lg:flex-row gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-16 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
          
          {/* Search & Filter Header */}
          <div className="bg-gray-50 p-8 md:p-12 border-b-8 border-navy flex flex-col md:flex-row items-center gap-8 shadow-sm">
             <div className="flex-1 relative w-full">
                <input 
                  type="text" 
                  placeholder="Search by name, department..."
                  className="w-full bg-white border border-gray-200 px-6 py-4 text-sm font-bold text-navy outline-none focus:border-gold transition-all rounded-sm"
                />
                <Search className="absolute right-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
             </div>
             <button className="bg-navy text-white px-10 py-4 font-black uppercase tracking-widest text-xs flex items-center gap-3 hover:bg-navy-light transition-all whitespace-nowrap">
               <Filter className="w-4 h-4" /> Filter Results
             </button>
          </div>

          <div className="flex justify-between items-center border-b border-gray-100 pb-4">
             <span className="text-xs font-black uppercase tracking-widest text-gray-400">Showing {staff.length} Results</span>
             <div className="text-[10px] font-black uppercase tracking-[0.2em] text-gold animate-pulse">Live Directory</div>
          </div>

          {/* Staff Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {staff.map((member, idx) => (
              <div key={idx} className="bg-white border border-gray-100 p-8 group hover:shadow-2xl hover:border-gold transition-all duration-500 relative overflow-hidden flex flex-col">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gray-50 -translate-y-12 translate-x-12 rotate-45 group-hover:bg-gold/10 transition-colors"></div>
                
                <div className="flex items-center gap-4 mb-6 relative z-10">
                  <img
                    src={`https://i.pravatar.cc/64?u=${encodeURIComponent(member.name)}`}
                    alt={member.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-gold/30 flex-shrink-0"
                  />
                  <div className="space-y-1">
                    <h3 className="text-2xl font-black text-navy uppercase tracking-tighter leading-none group-hover:text-gold transition-colors">{member.name}</h3>
                    <div className="text-gold text-[10px] font-black uppercase tracking-[0.2em]">{member.title}</div>
                  </div>
                </div>

                <div className="space-y-4 relative z-10 flex-1">
                   <div className="flex items-center gap-4 p-3 bg-gray-50 group-hover:bg-white border border-transparent group-hover:border-gray-100 transition-all">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="text-xs font-bold text-navy uppercase tracking-widest">{member.department}</span>
                   </div>
                   
                   <div className="space-y-2 pt-4">
                      <a href={`mailto:${member.email}`} className="flex items-center gap-3 text-sm font-medium text-gray-500 hover:text-gold transition-colors">
                         <Mail className="w-4 h-4" /> {member.email}
                      </a>
                      <a href={`tel:${member.phone}`} className="flex items-center gap-3 text-sm font-medium text-gray-500 hover:text-gold transition-colors">
                         <Phone className="w-4 h-4" /> {member.phone}
                      </a>
                   </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-50 flex justify-between items-center relative z-10">
                   <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
                      <MapPin className="w-3 h-3 text-gold" /> {member.campus}
                   </div>
                   <button className="w-8 h-8 rounded-full bg-navy/5 flex items-center justify-center text-navy group-hover:bg-gold group-hover:text-white transition-all">
                      <Users className="w-4 h-4" />
                   </button>
                </div>
              </div>
            ))}
          </div>

        </div>
      </main>
    </div>
  );
}
